<template>
  <div class="contacts-container">
    <!-- 搜索入口 -->
    <div class="search-entry-row" v-if="!searchMode">
      <div class="search-entry-inner" @click="enterSearchMode">
        <van-icon name="search" size="14" color="#8A8A8A" />
        <span>{{ $t('chatMsgSearch_placeholder') }}</span>
      </div>
    </div>

    <!-- 搜索面板：搜消息（所有联系人的私聊）/ 按用户名搜联系人 -->
    <div class="search-panel" v-if="searchMode">
      <div class="search-input-row">
        <div class="search-input-inner">
          <van-icon name="search" size="14" color="#8A8A8A" />
          <input v-model="searchQuery" ref="searchInput" type="text" class="search-input"
            :placeholder="$t('chatMsgSearch_placeholder')" @keyup.enter="onSearchEnter" />
          <van-icon v-if="searchQuery" name="clear" size="14" color="#C0C4CC" @click="clearSearchQuery" />
        </div>
        <span class="search-cancel-btn" @click="exitSearchMode">{{ $t('cancel') }}</span>
      </div>
      <div class="search-tab-row">
        <div class="search-tab" :class="{ active: searchTab == 'msg' }" @click="switchSearchTab('msg')">{{ $t('chatMsgSearch_tabMsgs') }}</div>
        <div class="search-tab" :class="{ active: searchTab == 'contact' }" @click="switchSearchTab('contact')">{{ $t('chatMsgSearch_tabContacts') }}</div>
      </div>
      <div class="search-result-wrapper">
        <!-- 消息搜索结果 -->
        <template v-if="searchTab == 'msg'">
          <div v-if="msgSearchLoading" class="search-status-row">{{ $t('loading') }}</div>
          <div v-else-if="!searchQuery.trim()" class="search-empty-hint">{{ $t('chatMsgSearch_emptyPrompt') }}</div>
          <div v-else-if="!searchKeywordValid" class="search-empty-hint">{{ $t('chatMsgSearch_tooShort') }}</div>
          <div v-else-if="msgResults.length == 0" class="search-empty-hint">{{ $t('noData') }}</div>
          <div v-for="m in msgResults" :key="m.msgId" class="contact-item" @click="onMsgResultClicked(m)">
            <div class="contact-avatar">
              <img v-if="m.peerUserAvatar" :src="$common.getFullImageUrl(m.peerUserAvatar, $config, 's')"
                style="width: 48px; height: 48px; border-radius: 16%;" />
              <div v-else class="avatar-placeholder">{{ (m.peerUserName || $t('anonymous'))[0] }}</div>
            </div>
            <div class="contact-content">
              <div class="contact-name">{{ m.peerUserName || $t('anonymous') }}</div>
              <div class="contact-last-msg" v-html="getLastMsgText(m)"></div>
            </div>
            <div class="contact-right-section">
              <div class="contact-time">{{ $common.formatSmartTime(m.createdAt, $i18n.locale) }}</div>
            </div>
          </div>
        </template>
        <!-- 联系人搜索结果（后端拉全量联系人，按用户名/邮箱 前缀优先、包含其次 匹配） -->
        <template v-else>
          <div v-if="contactSearchLoading" class="search-status-row">{{ $t('loading') }}</div>
          <div v-else-if="!searchQuery.trim()" class="search-empty-hint">{{ $t('chatMsgSearch_contactEmptyPrompt') }}</div>
          <div v-else-if="!searchKeywordValid" class="search-empty-hint">{{ $t('chatMsgSearch_tooShort') }}</div>
          <div v-else-if="contactResults.length == 0" class="search-empty-hint">{{ $t('chatMsgSearch_noContacts') }}</div>
          <div v-for="c in contactResults" :key="c.userId" class="contact-item" @click="onContactResultClicked(c)">
            <div class="contact-avatar">
              <img v-if="c.avatar" :src="$common.getFullImageUrl(c.avatar, $config, 's')"
                style="width: 48px; height: 48px; border-radius: 16%;" />
              <div v-else class="avatar-placeholder">{{ (c.userName || c.email || $t('anonymous'))[0] }}</div>
            </div>
            <div class="contact-content">
              <div class="contact-name">{{ c.userName || c.email || c.userId }}</div>
              <div class="contact-last-msg">{{ c.email }}</div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div class="contact-list-wrapper" v-if="!searchMode">
      <van-pull-refresh v-model="refreshing" :loading-text="$t('loading')" @refresh="onRefresh">
        <van-list v-model="loading" :finished="finished" :loading-text="$t('loading')"
          @load="onLoad">
          <!-- 一键全部已读 -->
          <div class="mark-all-read-row" v-if="$store.state.hasUnreadMsg || $store.state.hasUnreadInteraction">
            <span class="mark-all-read-btn" @click="markAllRead">{{ $t('mark_all_read') }}</span>
          </div>

          <!-- 互动通知置顶卡片 -->
          <div class="contact-item interaction-notification-card" @click="showInteractionNotificationPopup = true">
            <div class="contact-avatar">
              <div class="interaction-avatar">
                <svg viewBox="0 0 24 24" fill="none" style="width: 22px; height: 22px; color: white;">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="currentColor"/>
                </svg>
              </div>
            </div>
            <div class="contact-content">
              <div class="contact-name">{{ $t('interaction_notification_title') }}</div>
              <div class="contact-last-msg" :class="{ 'in-preview-active': latestInteractionPreview }" v-html="latestInteractionPreview || $t('interaction_notification_subtitle')"></div>
            </div>
            <div class="contact-right-section">
              <span v-if="$store.state.unreadInteractionCount > 0" class="unread-count">{{ $store.state.unreadInteractionCount > 99 ? '99+' : $store.state.unreadInteractionCount }}</span>
              <div v-else-if="$store.state.hasUnreadInteraction" class="unread-dot"></div>
            </div>
          </div>

          <div v-for="contact in contacts" :key="contact.chatContactId" class="contact-item"
            @click="onContactItemClicked(contact)"
            @touchstart="onContactTouchStart(contact)"
            @touchend="onContactTouchEnd"
            @touchmove="onContactTouchEnd"
            @contextmenu.prevent="openContactActions(contact)">
            <div class="contact-avatar" style="cursor: pointer;" @click.stop="onContactAvatarClicked(contact)">
              <img v-if="contact.targetAvatar" :src="$common.getFullImageUrl(contact.targetAvatar, $config, 's')"
                style="width: 48px; height: 48px; border-radius: 16%;" />
              <div v-else class="avatar-placeholder">{{ (contact.targetUsername || contact.targetEmail ||
                $t('anonymous'))[0] }}</div>
            </div>
            <div class="contact-content">
              <div class="contact-name">
                {{ contact.targetUsername || contact.targetEmail || contact.targetUserId }}
              </div>
              <div class="contact-last-msg" v-if="contact.lastChatMsg" v-html="getLastMsgText(contact.lastChatMsg)">
              </div>
            </div>
            <div class="contact-right-section">
              <!-- 免打扰联系人固定红点（不显示数字，未读不计入 tab 总数） -->
              <div v-if="isDndContact(contact) && contact.lastChatMsg && contact.lastChatMsg.msgId && contact.lastChatMsg.read !== '1'" class="unread-dot"></div>
              <span v-else-if="getContactUnreadCount(contact) > 0" class="unread-count">{{ getContactUnreadCount(contact) > 99 ? '99+' : getContactUnreadCount(contact) }}</span>
              <div v-else-if="contact.lastChatMsg && contact.lastChatMsg.msgId && contact.lastChatMsg.read !== '1'" class="unread-dot"></div>
              <div class="contact-time" v-if="contact.lastChatMsg">
                {{ $common.formatSmartTime(contact.lastChatMsg.createdAt, $i18n.locale) }}
              </div>
              <div class="contact-arrow">
                <van-icon name="arrow" size="14" color="#999" />
              </div>
            </div>
          </div>

          <!-- Empty state -->
        </van-list>
      </van-pull-refresh>
    </div>

    <!-- 聊天弹窗 -->
    <van-popup v-model="showChatPopup" position="bottom" round :style="{ height: '80%', width: '100%' }">
      <div class="chat-popup">
        <div class="chat-popup-header">
          <div class="app-hint-pill" @click="showAppDownloadPopup = true">
            <svg viewBox="0 0 24 24" fill="none" style="width: 11px; height: 11px;">
              <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" stroke-width="2"/>
              <line x1="10" y1="18" x2="14" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            {{ $t('hint_get_app') }}
          </div>
          <div class="chat-popup-title" v-if="currentContact">{{ $t('chat_with', {name: currentContactName}) }}</div>
          <div style="display: flex; align-items: center; gap: 12px;">
            <van-button type="info" size="mini" @click="showMoreContacts = true">{{ $t('more') }}</van-button>
            <van-icon name="cross" size="20" color="#666" @click="closeChatPopup" />
          </div>
        </div>
        <!-- ChatBox 组件 -->
        <div class="chat-popup-body">
          <ChatBox v-if="showChatPopup" :targetUserId="currentContact ? currentContact.targetUserId : ''"
            :targetUserAvatar="currentContact ? currentContact.targetAvatar : ''" :targetUserName="currentContactName"
            :currentAvatarUrl="$store.state.user && $store.state.user.avatar"
            :currentUserName="$store.state.user && $store.state.user.userName"
            :initialLocateMsgId="locateMsgId"
            :chatMsgListStyle="{ minHeight: 'auto', maxHeight: 'none', backgroundColor: '#ECEAE6', padding: '12px' }"
            :showRequirementButtons="false" :showUserMsgBanner="false" chatBoxType="" />
        </div>
      </div>
    </van-popup>

    <!-- App 下载提示弹窗 -->
    <van-popup v-model="showAppDownloadPopup" round style="padding: 20px; width: 260px; text-align: center;">
      <svg viewBox="0 0 24 24" fill="none" style="width: 40px; height: 40px; color: #E8543A; margin: 0 auto 8px;">
        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="2"/>
        <path d="M12 18V12M12 12L9 9M12 12L15 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <div style="font-weight: bold; font-size: 16px; color: #1A1A1A; margin-bottom: 4px;">{{ $t('stay_connected') }}</div>
      <div style="font-size: 13px; color: #8A8A8A; margin-bottom: 16px;">{{ $t('download_app_desc') }}</div>
      <div style="display: flex; flex-direction: column; gap: 8px;">
        <a :href="appStoreUrl" target="_blank" class="app-download-btn app-store">
          <svg viewBox="0 0 24 24" fill="currentColor" style="width: 16px; height: 16px;">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.21-1.98 1.07-3.11-1.05.05-2.31.7-3.06 1.53-.68.74-1.26 1.93-1.1 3.07 1.18.09 2.38-.59 3.09-1.49z"/>
          </svg>
          {{ $t('app_store') }}
        </a>
        <a :href="googlePlayUrl" target="_blank" class="app-download-btn google-play">
          <svg viewBox="0 0 24 24" fill="currentColor" style="width: 16px; height: 16px;">
            <path d="M3 20.5V3.5c0-.74.8-1.21 1.45-.88l15.84 8.5c.66.35.66 1.31 0 1.66L4.45 21.38c-.65.33-1.45-.14-1.45-.88z"/>
          </svg>
          {{ $t('google_play') }}
        </a>
        <a :href="apkUrl" target="_blank" class="app-download-btn apk" download>
          <svg viewBox="0 0 24 24" fill="currentColor" style="width: 16px; height: 16px;">
            <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z"/>
          </svg>
          {{ $t('direct_apk') }}
        </a>
      </div>
    </van-popup>

    <!-- 更多联系方式弹窗 -->
    <ContactMethods :visible.sync="showMoreContacts" :contactInfo="currentContactInfo"
      :targetUserId="currentContact ? currentContact.targetUserId : ''" :userName="currentContactName"
      :userAvatar="currentContactInfo.avatar || (currentContact ? currentContact.targetAvatar : '')"
      :userBio="currentContactInfo.bio || ''" />

    <!-- 长按联系人操作（免打扰开关） -->
    <van-action-sheet v-model="showContactActions" :actions="contactActions"
      :cancel-text="$t('cancel')" close-on-click-action
      @select="onContactActionSelect" @cancel="showContactActions = false" />

    <!-- 互动通知弹窗 -->
    <van-popup v-model="showInteractionNotificationPopup" position="right" :style="{ width: '100%', height: '100%' }" @close="onInteractionNotificationClose">
      <InteractionNotification v-if="showInteractionNotificationPopup" @close="onInteractionNotificationClose" />
    </van-popup>
  </div>
</template>

<script>
import axios from 'axios'
import Vue from 'vue'
import twemoji from 'twemoji'
import { List, PullRefresh, Empty, Icon, Toast, Popup, Button, ActionSheet } from 'vant'
import ChatBox from './ChatBox.vue'
import ContactMethods from './ContactMethods.vue'
import InteractionNotification from './InteractionNotification.vue'
import wsService from '@/services/websocket.js'

Vue.use(List)
Vue.use(PullRefresh)
Vue.use(Empty)
Vue.use(ActionSheet)
Vue.use(Icon)
Vue.use(Toast)
Vue.use(Popup)
Vue.use(Button)

export default {
  name: 'Contacts',
  components: {
    ChatBox,
    ContactMethods,
    InteractionNotification
  },
  props: {
    msg: String
  },
  data() {
    return {
      contacts: [],
      loading: false,
      finished: false,
      refreshing: false,
      curPage: 1,
      pageSize: 20,
      showChatPopup: false,
      currentContact: null,
      showMoreContacts: false,
      currentContactInfo: {},
      // App 下载提示弹窗
      showAppDownloadPopup: false,
      appStoreUrl: 'https://apps.apple.com/us/app/octopie-ai/id6760101997',
      googlePlayUrl: 'https://play.google.com/store/apps/details?id=ai.octopie.client',
      apkUrl: 'https://download.octosay.ai/octopie_ai.apk',
      showInteractionNotificationPopup: false,
      latestInteractionPreview: '',
      // 长按联系人 → 免打扰
      showContactActions: false,
      actionContact: null,
      longPressTimer: null,
      longPressFired: false,
      // 消息/联系人搜索
      searchMode: false,
      searchQuery: '',
      searchTab: 'msg',
      msgResults: [],
      msgSearchLoading: false,
      contactResults: [],
      contactSearchLoading: false,
      searchDebounceTimer: null,
      // 从搜索结果进入聊天时，需要定位的消息 ID（传给 ChatBox）
      locateMsgId: ''
    }
  },
  computed: {
    // 关键词是否满足搜索条件：至少 2 个字（rune），与后端 ngram 最小 token=2 对齐，单字必空结果
    searchKeywordValid() {
      return Array.from((this.searchQuery || '').trim()).length >= 2
    },
    contactActions() {
      const isDnd = this.actionContact ? this.isDndContact(this.actionContact) : false
      return [{ name: isDnd ? this.$t('dnd_action_disable') : this.$t('dnd_action_enable'), key: 'dnd' }]
    },
    beforeEatchInitialized() {
      return this.$store.state.beforeEatchInitialized
    },
    currentContactName() {
      if (!this.currentContact) {
        return this.$t('default_username')
      }
      return this.currentContact.targetUsername || this.currentContact.targetEmail || this.currentContact.targetUserId || this.$t('default_username')
    }
  },
  watch: {
    searchQuery() {
      // 关键词变化：防抖 400ms 后同时跑「消息 + 联系人」两个搜索（各自独立结果，切 tab 不重搜）
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.runSearchNow()
      }, 400)
    },
    beforeEatchInitialized(newValue) {
      if (newValue) {
        this.onRefresh()
      }
    },
    showChatPopup(newValue) {
      if (!newValue) {
        // 关闭聊天弹窗后，重新从后端刷新私聊未读状态，避免 tab 红点滞后
        this.refreshGlobalChatUnread()
      }
    }
  },
  mounted() {
    if (this.$store.state.beforeEatchInitialized) {
      this.onRefresh()
    }
    this.setupWebSocketListeners()
  },
  beforeDestroy() {
    this.removeWebSocketListeners()
  },
  methods: {
    // ============ 消息/联系人搜索 ============
    enterSearchMode() {
      this.searchMode = true
      this.searchQuery = ''
      this.searchTab = 'msg'
      this.msgResults = []
      this.contactResults = []
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus()
        }
      })
    },
    exitSearchMode() {
      this.searchMode = false
      this.clearSearchQuery()
    },
    switchSearchTab(tab) {
      this.searchTab = tab
    },
    onSearchEnter() {
      this.runSearchNow()
    },
    clearSearchQuery() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
        this.searchDebounceTimer = null
      }
      this.searchQuery = ''
      this.msgResults = []
      this.contactResults = []
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus()
        }
      })
    },
    // 关键词就绪：同时跑「消息 + 联系人」两个搜索（至少 2 个字，与后端 ngram 最小 token=2 对齐，单字必空结果）
    runSearchNow() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
        this.searchDebounceTimer = null
      }
      const kw = (this.searchQuery || '').trim()
      if (!kw || Array.from(kw).length < 2) {
        this.msgResults = []
        this.contactResults = []
        return
      }
      this.doMsgSearch()
      this.doContactSearch()
    },
    // 搜所有联系人下的私聊消息（MySQL 全文搜索，后端已排除平台AI号）
    async doMsgSearch() {
      const kw = (this.searchQuery || '').trim()
      if (!kw) {
        return
      }
      this.msgSearchLoading = true
      try {
        const response = await axios.post('/api/chat/search_chat_msgs', { keyword: kw, curPage: 1, pageSize: 20 })
        if (response.data && response.data.code === 0) {
          this.msgResults = response.data.data.chatMsgs || []
        }
      } catch (error) {
        console.log('search chat msgs failed:', error)
      } finally {
        this.msgSearchLoading = false
      }
    },
    // 点击消息搜索结果：跳进对应私聊（已有联系人直接用，否则构造临时联系人）
    onMsgResultClicked(m) {
      let target = this.contacts.find(c => String(c.targetUserId) === String(m.peerUserId))
      if (!target) {
        target = {
          chatContactId: 'search_' + m.peerUserId,
          targetUserId: m.peerUserId,
          targetUsername: m.peerUserName,
          targetEmail: '',
          targetAvatar: m.peerUserAvatar,
          lastChatMsg: null
        }
      }
      this.onContactClicked(target, m.msgId)
    },
    // 搜联系人（后端拉全量联系人 userId、批量取用户信息后按 用户名/邮箱 前缀优先、包含其次 匹配）
    async doContactSearch() {
      const kw = (this.searchQuery || '').trim()
      if (!kw) {
        return
      }
      this.contactSearchLoading = true
      try {
        const response = await axios.post('/api/chat/search_chat_contacts', { keyword: kw })
        if (response.data && response.data.code == 0) {
          this.contactResults = response.data.data.chatContacts || []
        }
      } catch (error) {
        console.log('search chat contacts failed:', error)
      } finally {
        this.contactSearchLoading = false
      }
    },
    // 点击联系人搜索结果：进私聊（已有联系人直接用，否则构造临时联系人）
    onContactResultClicked(c) {
      let target = this.contacts.find(ct => String(ct.targetUserId) === String(c.userId))
      if (!target) {
        target = {
          chatContactId: 'search_' + c.userId,
          targetUserId: c.userId,
          targetUsername: c.userName,
          targetEmail: c.email || '',
          targetAvatar: c.avatar || '',
          lastChatMsg: {}
        }
      }
      this.onContactClicked(target)
    },

    onRefresh() {
      this.contacts = []
      this.curPage = 1
      this.finished = false
      this.refreshing = true
      this.onLoad()
    },

    onLoad() {
      let vm = this
      if (vm.finished || vm.loading) {
        vm.refreshing = false
        return
      }

      if (!vm.$common.getToken()) {
        vm.finished = true
        vm.loading = false
        vm.refreshing = false
        return
      }

      vm.loading = true

      var url = '/api/chat/get_chat_contacts'
      var req = {
        curPage: vm.curPage,
        pageSize: vm.pageSize
      }

      axios.post(url, req).then(
        response => {
          vm.loading = false
          vm.refreshing = false

          var body = response.data
          if (body.code !== 0) {
            Toast.fail(vm.$t('toast_load_contacts_failed', {msg: body.msg}))
            vm.finished = true
            return
          }

          var data = body.data
          if (data.chatContacts && data.chatContacts.length > 0) {
            // 处理联系人列表
            data.chatContacts.forEach(contact => {
              if (contact.lastChatMsg && contact.lastChatMsg.msgId) {
                const msg = contact.lastChatMsg
                // 如果最后这条消息是自己向对方发的消息，那肯定是已读
                if (msg.toUserId === contact.targetUserId) {
                  contact.lastChatMsg.read = '1'
                }
              }
            })

            vm.contacts = vm.contacts.concat(data.chatContacts)
            // 同步免打扰映射（App.vue 的 WS handler 据此跳过免打扰发送方，避免按钮闪现）
            vm.$store.commit('setContactsDnd', data.chatContacts)
            if (data.chatContacts.length < data.pageSize) {
              vm.finished = true
            }
            vm.curPage = parseInt(data.curPage) + 1
          } else {
            vm.finished = true
          }

          // 更新未读消息状态
          vm.updateUnreadStatus()
        }
      ).catch(error => {
        console.log(error)
        vm.loading = false
        vm.refreshing = false
        vm.finished = true
        Toast.fail(vm.$t('toast_load_contacts_error'))
      })
    },

    async onContactClicked(contact, locateMsgId) {
      this.currentContact = contact
      // 从搜索结果进入时带上要定位的消息 ID（普通入口不传，为空则不定位）
      this.locateMsgId = locateMsgId || ''
      this.showChatPopup = true
      await this.loadContactUserInfo()
    },

    // 关闭聊天弹窗：顺手清掉定位消息 ID，避免下次打开其他联系人时残留误触发定位
    closeChatPopup() {
      this.showChatPopup = false
      this.locateMsgId = ''
    },

    // 点击联系人头像，查看对方 profile（不进入聊天）
    async onContactAvatarClicked(contact) {
      this.currentContact = contact
      this.showMoreContacts = true
      await this.loadContactUserInfo()
    },

    async loadContactUserInfo() {
      if (!this.currentContact || !this.currentContact.targetUserId) {
        return
      }
      try {
        const response = await axios.post('/api/user/get_basic_user_info', {
          targetUserId: this.currentContact.targetUserId
        })
        if (response.data && response.data.code === 0 && response.data.data) {
          const user = response.data.data
          this.currentContactInfo = {
            email: user.email || '',
            mobile: user.mobile || '',
            whatsapp: user.whatsapp || '',
            telegram: user.telegram || '',
            wechat: user.wechat || '',
            discord: user.discord || '',
            line: user.line || '',
            signal: user.signal || '',
            avatar: user.avatar || '',
            username: user.userName || '',
            bio: user.bio || ''
          }
        }
      } catch (error) {
        console.error('Failed to load contact user info:', error)
      }
    },

    getLastMsgText(lastChatMsg) {
      if (!lastChatMsg) {
        return ''
      }
      // 已撤回消息：不展示原内容，显示撤回提示
      if (lastChatMsg.revoked == '1') {
        // revokedBy=2 为平台处置（内容安全人工复核拦截），区别于本人撤回
        return lastChatMsg.revokedBy == '2' ? this.$t('msg_revoked_platform') : (lastChatMsg.direction == '0' ? this.$t('msg_revoked_self') : this.$t('msg_revoked_other'))
      }
      if (!lastChatMsg.msg) {
        return ''
      }
      if (lastChatMsg.contentType === 'plaintext' || lastChatMsg.contentType === 'text') {
        return this.parseEmoji(lastChatMsg.msg)
      } else if (lastChatMsg.contentType === 'mixed') {
        try {
          const contentArray = Array.isArray(lastChatMsg.msg) ? lastChatMsg.msg : JSON.parse(lastChatMsg.msg)
          // 生成友好的摘要
          let summary = ''
          let hasText = false
          let textContent = ''
          let imgCount = 0
          let fileCount = 0
          let locationCount = 0
          let voiceCount = 0
          let videoCount = 0
          let emojis = []

          // 统计各类型数量
          contentArray.forEach(item => {
            if (item.type === 'plaintext' && item.content) {
              hasText = true
              textContent = item.content.replace(/<[^>]*>/g, '')
            } else if (item.type === 'img') {
              imgCount++
            } else if (item.type === 'file') {
              fileCount++
            } else if (item.type === 'video') {
              videoCount++
            } else if (item.type === 'audio') {
              voiceCount++
            } else if (item.type === 'pick_location') {
              locationCount++
            } else if (item.type === 'emoji') {
              emojis.push(item.content)
            }
          })

          // 构建摘要
          let prefix = ''
          if (imgCount > 0) {
            prefix += imgCount === 1 ? '[Image] ' : '[Imagex' + imgCount + '] '
          }
          if (fileCount > 0) {
            prefix += fileCount === 1 ? '[File] ' : '[Filex' + fileCount + '] '
          }
          if (videoCount > 0) {
            prefix += videoCount === 1 ? '[Video] ' : '[Videox' + videoCount + '] '
          }
          if (voiceCount > 0) {
            prefix += voiceCount === 1 ? '[Voice] ' : '[Voicex' + voiceCount + '] '
          }
          if (locationCount > 0) {
            prefix += locationCount === 1 ? '[Location] ' : '[Locationx' + locationCount + '] '
          }
          // emoji 直接渲染显示
          let emojiHtml = ''
          if (emojis.length > 0) {
            emojiHtml = emojis.map(e => this.parseEmoji(e)).join('')
          }

          if (hasText && textContent) {
            // 有文本内容，显示前缀+emoji+文本（限制总长度）
            const maxLen = 50
            const textWithEmoji = prefix + emojiHtml + textContent
            const truncatedText = textWithEmoji.length > maxLen ? textWithEmoji.substring(0, maxLen) + '...' : textWithEmoji
            summary = truncatedText
          } else if (emojis.length > 0) {
            // 只有 emoji
            summary = prefix + emojiHtml
          } else {
            // 只有其他附件
            summary = prefix.trim()
          }

          return summary || '[Mixed]'
        } catch (e) {
          return '[Mixed]'
        }
      } else {
        return '[Other]'
      }
    },
    // 解析emoji为twemoji图片
    parseEmoji(text) {
      // 先反转义 HTML 实体（如 &lt; &gt; &amp;）
      if (text && (text.includes('&lt;') || text.includes('&gt;') || text.includes('&amp;'))) {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = text
        text = tempDiv.textContent
      }
      return twemoji.parse(text, {
        className: 'emoji-summary-img',
        base: this.$publicPath + 'static/twemoji/',
        size: 'svg',
        ext: '.svg'
      })
    },



    // 行内未读数字（long 按 string 传，旧后端可能无此字段，返回 0 走红点降级）
    getContactUnreadCount(contact) {
      return parseInt(contact.unreadMsgCount, 10) || 0
    },

    // 该联系人是否被当前用户设为免打扰（long 按 string 传）
    isDndContact(contact) {
      return parseInt(contact.dnd, 10) === 1
    },

    // 长按（500ms）打开联系人操作；滚动时取消
    onContactTouchStart(contact) {
      this.longPressFired = false
      this.longPressTimer = setTimeout(() => {
        this.longPressFired = true
        this.openContactActions(contact)
      }, 500)
    },
    onContactTouchEnd() {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer)
        this.longPressTimer = null
      }
    },
    // 桌面端右键
    openContactActions(contact) {
      this.actionContact = contact
      this.showContactActions = true
    },
    // 长按松开时不再触发点击进聊天
    onContactItemClicked(contact) {
      if (this.longPressFired) {
        this.longPressFired = false
        return
      }
      this.onContactClicked(contact)
    },
    async onContactActionSelect() {
      this.showContactActions = false
      if (!this.actionContact) return
      const contact = this.actionContact
      const target = !this.isDndContact(contact)
      try {
        await axios.post('/api/chat/update_contact_dnd', {
          targetUserId: contact.targetUserId,
          dnd: target ? 1 : 0
        })
        contact.dnd = target ? 1 : 0
        // 同步免打扰映射（WS handler 跳过判断用）
        this.$store.commit('setContactsDnd', { userId: contact.targetUserId, dnd: target ? 1 : 0 })
        Toast.success(target ? this.$t('dnd_enabled') : this.$t('dnd_disabled'))
        // 免打扰变更影响 tab 未读数，重新拉真实值
        this.refreshGlobalChatUnread()
      } catch (error) {
        console.log('update dnd failed:', error)
        Toast.fail(this.$t('toast_mark_all_read_failed'))
      }
    },

    // 一键全部已读：私聊消息 + 互动通知，成功后本地乐观更新
    async markAllRead() {
      try {
        await Promise.all([
          axios.post('/api/chat/mark_all_msgs_read', {}),
          axios.post('/api/interaction_notification/mark_all_read', {})
        ])
        this.contacts.forEach(contact => {
          if (contact.lastChatMsg && contact.lastChatMsg.msgId) {
            contact.lastChatMsg.read = '1'
          }
          contact.unreadMsgCount = 0
        })
        this.$store.commit('setUnreadChatCount', 0)
        this.$store.commit('setUnreadInteractionCount', 0)
        this.$store.commit('setHasUnreadChatMsg', false)
        this.$store.commit('setHasUnreadMsg', false)
        this.$store.commit('setHasUnreadInteraction', false)
      } catch (error) {
        console.log('markAllRead failed:', error)
        Toast.fail(this.$t('toast_mark_all_read_failed'))
      }
    },

    updateUnreadStatus() {
      // 免打扰联系人不点亮红点/按钮（其未读不计数，仅行内红点）
      const hasUnread = this.contacts.some(contact =>
        !this.isDndContact(contact) &&
        contact.lastChatMsg && contact.lastChatMsg.msgId && contact.lastChatMsg.read !== '1'
      )
      if (hasUnread) {
        // 当前页已加载的联系人有未读，直接置红点
        this.$store.commit('setHasUnreadChatMsg', true)
      } else {
        // 分页列表不能证明"全部已读"，以后端 check_have_unread_msg 为准重新刷新
        this.refreshGlobalChatUnread()
      }
      // 同时检查互动通知未读状态
      this.checkUnreadInteraction()
    },
    // 从后端重新获取私聊未读状态，作为全局红点的唯一来源
    refreshGlobalChatUnread() {
      if (!this.$common.getToken()) {
        return
      }
      axios.post('/api/chat/check_have_unread_msg', {
        chatType: 'private'
      }).then(response => {
        const body = response.data
        if (body.code === 0 && body.data) {
          const hasUnread = body.data.haveUnreadMsg == 1 || body.data.haveUnreadMsg === '1'
          this.$store.commit('setUnreadChatCount', body.data.unreadChatCount)
          this.$store.commit('setHasUnreadChatMsg', hasUnread)
          this.$store.commit('setHasUnreadMsg', hasUnread)
        }
      }).catch(error => {
        console.log('Failed to refresh global chat unread:', error)
      })
    },
    checkUnreadInteraction() {
      if (!this.$common.getToken()) {
        return
      }
      axios.post('/api/interaction_notification/check_have_unread', {}).then(response => {
        const body = response.data
        if (body.code === 0 && body.data) {
          const hasUnread = body.data.haveUnreadInteraction == 1 || body.data.haveUnreadInteraction === '1'
          // 未读通知条数用于 messages tab 数字 badge
          this.$store.commit('setUnreadInteractionCount', body.data.unreadInteractionCount)
          this.$store.commit('setHasUnreadInteraction', hasUnread)
          // 如果有未读且还没有预览，获取最新一条通知作为预览
          if (hasUnread && !this.latestInteractionPreview) {
            this.loadLatestInteractionPreview()
          }
        }
      }).catch(error => {
        console.log('Failed to check unread interaction:', error)
      })
    },
    loadLatestInteractionPreview() {
      axios.post('/api/interaction_notification/list', {
        curPage: 1,
        pageSize: 1
      }).then(response => {
        const body = response.data
        if (body.code === 0 && body.data && body.data.interactionNotifications && body.data.interactionNotifications.length > 0) {
          const latest = body.data.interactionNotifications[0]
          this.latestInteractionPreview = this.buildInteractionPreview(latest)
        }
      }).catch(error => {
        console.log('Failed to load latest interaction preview:', error)
      })
    },
    buildInteractionPreview(notification) {
      const type = notification.type || ''
      const fromUserName = notification.fromUserName || ''
      const content = notification.content || ''
      const name = fromUserName || this.$t('anonymous') || 'Someone'
      if (type === 'like') {
        return name + ' ' + this.$t('interaction_notification_like') + (content ? ': ' + content : '')
      } else if (type === 'comment') {
        return name + ' ' + this.$t('interaction_notification_comment', { content: '' }) + (content ? ': ' + content : '')
      } else if (type === 'reply') {
        return name + ' ' + this.$t('interaction_notification_reply', { content: '' }) + (content ? ': ' + content : '')
      } else if (type === 'bookmark') {
        return name + ' ' + this.$t('interaction_notification_bookmark') + (content ? ': ' + content : '')
      }
      return ''
    },
    setupWebSocketListeners() {
      wsService.on('interaction_notification', this.wsInteractionNotificationHandler, 'contacts_interaction')
      wsService.on('msg', this.wsMsgHandler, 'contacts_msg')
    },
    removeWebSocketListeners() {
      wsService.off('interaction_notification', 'contacts_interaction')
      wsService.off('msg', 'contacts_msg')
    },
    // 收到新私聊消息时，实时更新对应联系人的 lastChatMsg（preview + 红点）
    wsMsgHandler(msg) {
      if (!msg || !msg.msgId) {
        return
      }
      // 只处理私聊消息（群消息不在该列表展示），流式消息是分片增量，跳过
      if (msg.channelId || msg.isStream) {
        return
      }
      // 自己发出的消息不做未读处理
      const myUserId = (this.$store.state.user && this.$store.state.user.userId) || wsService.userId || ''
      if (myUserId && msg.fromUserId == myUserId) {
        return
      }

      const msgForContact = JSON.parse(JSON.stringify(msg))
      // 如果当前正打开与该用户的聊天弹窗，ChatBox 会发 endpoint_ack 标记已读
      const currentChatWithUserId = this.$store.state.currentChatWithUserId
      if (currentChatWithUserId && msg.fromUserId == currentChatWithUserId) {
        msgForContact.read = '1'
      }

      const idx = this.contacts.findIndex(contact => msg.fromUserId == contact.targetUserId)
      if (idx === -1) {
        // 列表里还没有该联系人（新联系人），参照 ChatBox 的逻辑创建临时联系人插到顶部
        const newContact = {
          chatContactId: 'temp_' + msg.fromUserId,
          targetUserId: msg.fromUserId,
          targetUsername: msg.fromUserName || msg.fromUserId,
          targetEmail: '',
          targetAvatar: msg.fromUserAvatar || '',
          fromUserId: msg.fromUserId,
          toUserId: myUserId,
          lastChatMsg: msgForContact
        }
        this.contacts.unshift(newContact)
        return
      }

      const contact = this.contacts[idx]
      contact.lastChatMsg = msgForContact
      // 将该联系人移到列表顶部
      this.contacts.splice(idx, 1)
      this.contacts.unshift(contact)
    },
    wsInteractionNotificationHandler(msg) {
      this.$store.commit('setHasUnreadInteraction', true)
      // 构建预览文本
      const emitType = msg.emitType || ''
      let preview = ''
      if (emitType === 'chat_session_like') {
        const likeUserName = msg.likeUserName || ''
        const userMsg = msg.userMsg || ''
        const name = likeUserName || 'Someone'
        preview = name + ' ' + this.$t('interaction_notification_like') + (userMsg ? ': ' + userMsg : '')
      } else if (emitType === 'chat_session_comment') {
        const commentUserName = msg.commentUserName || ''
        const commentContent = msg.commentContent || ''
        const name = commentUserName || 'Someone'
        preview = name + ' ' + this.$t('interaction_notification_comment', { content: '' }) + (commentContent ? ': ' + commentContent : '')
      } else if (emitType === 'chat_session_bookmark') {
        const bookmarkUserName = msg.bookmarkUserName || ''
        const userMsg = msg.userMsg || ''
        const name = bookmarkUserName || 'Someone'
        preview = name + ' ' + this.$t('interaction_notification_bookmark') + (userMsg ? ': ' + userMsg : '')
      }
      if (preview) {
        this.latestInteractionPreview = preview
      }
    },
    onInteractionNotificationClose() {
      this.showInteractionNotificationPopup = false
      // 关闭弹窗后重新检查未读状态，如果全部已读则清除预览
      this.checkUnreadInteraction()
      if (!this.$store.state.hasUnreadInteraction) {
        this.latestInteractionPreview = ''
      }
    }
  }
}
</script>

<style scoped>
.contacts-container {
  min-height: calc(100vh - 20px);
  background: #F7F5F2;
  padding-top: 10px;
}

.contact-list-wrapper {
  background: transparent;
}

.contact-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 4px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: background-color 0.2s;
}

.contact-item:active {
  background-color: #FAFAF8;
}

.interaction-notification-card {
  margin-bottom: 8px;
  border: 0.5px solid rgba(248, 160, 144, 0.5);
  background: linear-gradient(135deg, #FFF8F6, #FEF0ED);
}

.interaction-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #E8543A, #F07058);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(232, 84, 58, 0.25);
}

.interaction-new-badge {
  padding: 2px 6px;
  border-radius: 10px;
  background-color: #E05252;
  color: white;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
}

.contact-avatar {
  margin-right: 12px;
  flex-shrink: 0;
}

.avatar-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 16%;
  background-color: #E05252;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
}

.contact-content {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.contact-name {
  font-size: 15px;
  font-weight: 600;
  color: #1A1A1A;
  margin-bottom: 4px;
}

.contact-last-msg {
  font-size: 13px;
  color: #8A8A8A;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-last-msg.in-preview-active {
  color: #1A1A1A;
  font-weight: 500;
}

.contact-last-msg>>>.emoji-summary-img,
.contact-last-msg>>>.twemoji {
  width: 16px !important;
  height: 16px !important;
  vertical-align: middle;
  margin: 0 1px;
}

.contact-last-msg>>>.emoji-summary-img svg,
.contact-last-msg>>>.twemoji svg {
  width: 16px !important;
  height: 16px !important;
}

.contact-right-section {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #E05252;
}

.unread-count {
  min-width: 16px;
  height: 16px;
  line-height: 16px;
  padding: 0 4px;
  background: #E05252;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  border-radius: 8px;
  text-align: center;
  box-sizing: border-box;
  flex-shrink: 0;
}

.mark-all-read-row {
  display: flex;
  justify-content: flex-end;
  padding: 0 14px 4px;
}

.mark-all-read-btn {
  font-size: 12px;
  font-weight: 600;
  color: #C93E1A;
  background: #FEF0ED;
  border-radius: 14px;
  padding: 4px 10px;
  cursor: pointer;
  transition: background 0.2s;
}

.mark-all-read-btn:active {
  background: #F8A090;
}

.contact-time {
  font-size: 12px;
  color: #8A8A8A;
}

.contact-arrow {
  margin-left: 4px;
}

/* 搜索入口 + 搜索面板 */
.search-entry-row {
  padding: 4px 14px 8px;
}

.search-entry-inner {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #FFFFFF;
  border: 1px solid #E0DDD8;
  border-radius: 18px;
  padding: 8px 14px;
  color: #8A8A8A;
  font-size: 13px;
  cursor: pointer;
}

.search-panel {
  padding: 4px 14px 0;
}

.search-input-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.search-input-inner {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #FFFFFF;
  border: 1px solid #E0DDD8;
  border-radius: 18px;
  padding: 8px 12px;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #1A1A1A;
}

.search-cancel-btn {
  color: #C93E1A;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}

.search-tab-row {
  display: flex;
  gap: 24px;
  margin: 12px 2px 4px;
  border-bottom: 1px solid #E0DDD8;
}

.search-tab {
  padding: 6px 2px 8px;
  font-size: 14px;
  color: #4A4A4A;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}

.search-tab.active {
  color: #C93E1A;
  font-weight: 600;
  border-bottom-color: #C93E1A;
}

.search-result-wrapper {
  margin-top: 4px;
}

.search-status-row {
  display: flex;
  justify-content: center;
  padding: 16px 0;
  color: #8A8A8A;
  font-size: 13px;
}

.search-empty-hint {
  padding: 24px 0;
  text-align: center;
  color: #8A8A8A;
  font-size: 13px;
}

/* 聊天弹窗样式 */
.chat-popup {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #E0DDD8;
  flex-shrink: 0;
}

.chat-popup-title {
  font-size: 16px;
  font-weight: 600;
  color: #1A1A1A;
  flex: 1;
}

.chat-popup-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.app-hint-pill {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: linear-gradient(135deg, #E8543A, #C93E1A);
  color: #ffffff;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.chat-popup-header .app-hint-pill {
  padding: 5px 10px;
}

.app-hint-pill:hover {
  opacity: 0.9;
  transform: scale(1.02);
}

.app-download-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  text-decoration: none;
  transition: all 0.2s ease;
}

.app-store {
  background: #1A1A1A;
}

.app-store:hover {
  background: #1A1A1A;
}

.google-play {
  background: #3DAECF;
}

.google-play:hover {
  background: #2A8FAD;
}

.apk {
  background: #E8543A;
}

.apk:hover {
  background: #C93E1A;
}
</style>



// WEBPACK FOOTER //
// src/components/Contacts.vue